'use client';

/**
 * SSE Provider - React Context for Server-Sent Events
 *
 * Provides real-time status updates from compute-api and integrates
 * with React Query for automatic cache invalidation.
 *
 * For MODIFIED events that include a phase, the provider performs an
 * optimistic in-place update of the cached list data (avoiding a full
 * list API refetch). ADDED / DELETED events still trigger a full
 * cache invalidation so the list composition stays correct.
 *
 * Cache invalidation is route-aware: only resources relevant to the
 * current page route are invalidated to avoid unnecessary refetches.
 */
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { catalogKeys } from '@/features/catalogs/hooks/useCatalogs';
import { metadataServiceKeys } from '@/features/metadata-services/hooks/useMetadataServices';
import { queryServiceKeys } from '@/features/queryservices/hooks/useQueryServices';
import type { QueryService } from '@/features/queryservices/types/queryservice';
import {
  clusterSizeLabel,
  getRepresentativeExecutorCount,
} from '@/features/queryservices/utils/clusterSizeDerivation';
import { warmPoolKeys } from '@/features/warmpools/hooks/useWarmPools';
import { sseService } from '@/lib/sse/sseService';
import type {
  SSEConnectionState,
  SSEContextValue,
  SSEResourceType,
  SSERouteResourceConfig,
  SSEStatusEvent,
} from '@/lib/sse/types';
import { isTransitionalState } from '@/lib/utils/statusUtils';
import { SSE_CONFIG } from '@/config/sseConfig';

/**
 * Map SSE resource types to React Query keys for cache invalidation
 */
const resourceQueryKeyMap: Record<SSEResourceType, readonly string[]> = {
  QueryService: queryServiceKeys.all,
  MetadataServices: metadataServiceKeys.all,
  E6Catalog: catalogKeys.all,
  Pool: warmPoolKeys.all,
};

/**
 * Resource types that use lowercase status values (e.g. "running", "deploying").
 * All other resources use PascalCase (e.g. "Running", "Deploying").
 */
const LOWERCASE_STATUS_RESOURCES: SSEResourceType[] = ['QueryService'];

/**
 * Normalise the SSE phase string to match the casing convention used by
 * each resource type in the cached list data.
 */
function normalizePhaseForResource(phase: string, resource: SSEResourceType): string {
  if (LOWERCASE_STATUS_RESOURCES.includes(resource)) {
    return phase.toLowerCase();
  }
  // PascalCase: uppercase first char, rest as-is
  return phase.charAt(0).toUpperCase() + phase.slice(1);
}

/**
 * Attempt an in-place update of the resource's status inside every cached
 * list query for the given resource type. Returns `true` when at least one
 * cache entry was updated, `false` otherwise (caller should fall back to a
 * full invalidation).
 */
export function updateCachedResourceStatus(
  queryClient: QueryClient,
  event: SSEStatusEvent
): boolean {
  const queryKeys = resourceQueryKeyMap[event.resource];
  if (!queryKeys || !event.phase) return false;

  const normalizedPhase = normalizePhaseForResource(event.phase, event.resource);
  let updated = false;

  // Find all cached list queries for this resource type and patch them
  const listQueryKey = [...queryKeys, 'list'];
  const cache = queryClient.getQueriesData<{ items: Record<string, unknown>[]; total: number }>({
    queryKey: listQueryKey,
  });

  for (const [key, data] of cache) {
    if (!data?.items) continue;

    const idx = data.items.findIndex((item) => item.name === event.name);
    if (idx === -1) continue;

    const newItems = [...data.items];
    let patched: Record<string, unknown> = { ...newItems[idx], status: normalizedPhase };
    if (event.resource === 'QueryService') {
      patched = applyQueryServiceSizeFields(patched, event);
    }
    newItems[idx] = patched;

    queryClient.setQueryData(key, { ...data, items: newItems });
    updated = true;
  }

  return updated;
}

/**
 * Whether a QueryService event settles a cached list row in a new phase that
 * is not a transition, such as Running, Suspended or Failed. A cluster gains
 * or loses its error at that point (a failed resume ends in Suspended, a
 * reverted update in Running), and the event does not carry the error, so
 * the caller refetches the list. Call it before the in-place patch, which
 * overwrites the old phase.
 */
export function clusterSettledInNewPhase(queryClient: QueryClient, event: SSEStatusEvent): boolean {
  if (event.resource !== 'QueryService' || !event.phase || isTransitionalState(event.phase)) {
    return false;
  }
  const phase = event.phase.toLowerCase();
  const lists = queryClient.getQueriesData<{ items?: Record<string, unknown>[] }>({
    queryKey: [...resourceQueryKeyMap.QueryService, 'list'],
  });
  return lists.some(([, data]) =>
    data?.items?.some(
      (item) => item.name === event.name && String(item.status).toLowerCase() !== phase
    )
  );
}

/**
 * Apply the size/runtime fields carried by a QueryService SSE event (PLT-8787)
 * onto a cached list row, and recompute the UI-only `sizeLabel` so the Size
 * column updates live. The Active/Max column derives from readyExecutors /
 * executorReplicas / min / max in its accessor (PLT-9852), so patching those
 * is enough for it to refresh.
 *
 * Each field is applied only when the event carries it, preserving the row's
 * last-fetched values otherwise.
 */
function applyQueryServiceSizeFields(
  row: Record<string, unknown>,
  event: SSEStatusEvent
): Record<string, unknown> {
  const next = { ...row };
  if (event.baseSizeExecutors !== undefined) next.baseSizeExecutors = event.baseSizeExecutors;
  if (event.executorReplicas !== undefined) next.executorReplicas = event.executorReplicas;
  if (event.readyExecutors !== undefined) next.readyExecutors = event.readyExecutors;
  if (event.minExecutors !== undefined) next.minExecutors = event.minExecutors;
  if (event.maxExecutors !== undefined) next.maxExecutors = event.maxExecutors;

  const count =
    typeof next.baseSizeExecutors === 'number' && next.baseSizeExecutors > 0
      ? next.baseSizeExecutors
      : getRepresentativeExecutorCount(next as QueryService);
  if (count && count > 0) {
    next.sizeLabel = clusterSizeLabel(count);
  }
  return next;
}

/**
 * Route-to-resource mapping for SSE cache invalidation.
 *
 * Defines which SSE resource types should trigger cache invalidation
 * on each route. Routes not listed here will NOT receive any cache
 * invalidation from SSE events.
 *
 * Use '*' to invalidate all resource types on a given route.
 */
export const SSE_ROUTE_RESOURCE_MAP: SSERouteResourceConfig[] = [
  { pathPrefix: '/queryservices', resources: ['QueryService'] },
  { pathPrefix: '/metadata-services', resources: ['MetadataServices'] },
  { pathPrefix: '/catalogs', resources: ['E6Catalog'] },
  { pathPrefix: '/warmpools', resources: ['Pool'] },
  { pathPrefix: '/query-editor', resources: '*' },
];

/**
 * Check if a resource type should trigger cache invalidation for the given route.
 *
 * @param pathname - Current browser pathname
 * @param resource - SSE resource type from the event
 * @returns true if the event should trigger cache invalidation
 */
export function shouldInvalidateForRoute(pathname: string, resource: SSEResourceType): boolean {
  for (const config of SSE_ROUTE_RESOURCE_MAP) {
    if (pathname.startsWith(config.pathPrefix)) {
      return config.resources === '*' || config.resources.includes(resource);
    }
  }
  // Route not in the map — no invalidation
  return false;
}

// Create the context with null default
const SSEContext = createContext<SSEContextValue | null>(null);

interface SSEProviderProps {
  children: ReactNode;
}

/**
 * SSE Provider Component
 *
 * Wraps the application to provide SSE connection state and
 * automatic React Query cache invalidation on resource changes.
 *
 * Cache invalidation is gated by the current route — only resources
 * relevant to the active page trigger a refetch. State updates
 * (lastEvent, eventCount) still happen for all events so the
 * ConnectionIndicator and useResourceEvents hooks work regardless
 * of the current route.
 */
export function SSEProvider({ children }: SSEProviderProps) {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();

  // Connection state
  const [connectionState, setConnectionState] = useState<SSEConnectionState>('disconnected');
  const [clientId, setClientId] = useState<string | null>(null);
  const [lastEvent, setLastEvent] = useState<SSEStatusEvent | null>(null);
  const [lastHeartbeat, setLastHeartbeat] = useState<Date | null>(null);
  const [eventCount, setEventCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isEnabled] = useState(SSE_CONFIG.isEnabled);

  // Track initialization to prevent double-init in StrictMode
  const isInitialized = useRef(false);

  // Ref for current pathname so the handleStatusEvent callback
  // always reads the latest route without needing to be recreated
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  /**
   * Handle status events from SSE.
   *
   * State updates (lastEvent, eventCount) happen for every event.
   * Cache updates are gated by the current route via shouldInvalidateForRoute.
   *
   * For MODIFIED events that carry a `phase`, the handler performs an
   * optimistic in-place update of the cached list data so we skip the
   * full list API call. ADDED / DELETED events (and MODIFIED events
   * without a phase, or where the resource is not yet cached) still
   * trigger a full cache invalidation.
   */
  const handleStatusEvent = useCallback(
    (event: SSEStatusEvent) => {
      // Always update event state (powers ConnectionIndicator, useResourceEvents)
      setLastEvent(event);
      setEventCount((prev) => prev + 1);
      setError(null);

      // Only touch cache if current route cares about this resource type
      if (!shouldInvalidateForRoute(pathnameRef.current, event.resource)) {
        return;
      }

      const queryKeys = resourceQueryKeyMap[event.resource];
      if (!queryKeys) return;

      if (event.type === 'MODIFIED' && event.phase) {
        const settled = clusterSettledInNewPhase(queryClient, event);

        // Optimistic in-place update — skip the list API call
        const didUpdate = updateCachedResourceStatus(queryClient, event);

        if (!didUpdate) {
          // Resource not in cache yet; fall back to full invalidation
          queryClient.invalidateQueries({ queryKey: queryKeys, refetchType: 'all' });
        } else if (settled) {
          // Only the list response says whether the cluster's error changed
          void queryClient.invalidateQueries({ queryKey: [...queryKeys, 'list'] });
        }

        // Always invalidate the detail query so detail pages stay accurate
        const detailKey = [...queryKeys, 'detail', event.name];
        queryClient.invalidateQueries({ queryKey: detailKey });
      } else if (event.type === 'DELETED') {
        // List composition changed — full invalidation + remove detail
        queryClient.invalidateQueries({ queryKey: queryKeys, refetchType: 'all' });
        const detailKey = [...queryKeys, 'detail', event.name];
        queryClient.removeQueries({ queryKey: detailKey });
      } else {
        // ADDED or MODIFIED without phase — full invalidation
        queryClient.invalidateQueries({ queryKey: queryKeys, refetchType: 'all' });
        const detailKey = [...queryKeys, 'detail', event.name];
        queryClient.invalidateQueries({ queryKey: detailKey });
      }
    },
    [queryClient]
  );

  /**
   * Initialize SSE service and connect on mount
   */
  useEffect(() => {
    if (!isEnabled || isInitialized.current) {
      return;
    }

    isInitialized.current = true;

    // Initialize SSE service with callbacks
    sseService.init({
      onStateChange: setConnectionState,
      onConnected: (connectedEvent) => {
        setClientId(connectedEvent.clientId);
        setError(null);
        setEventCount(0); // Reset event count on new connection
        // Events may have been missed while offline. Refresh the current
        // query-service list and details as soon as the feed returns.
        void queryClient.invalidateQueries({ queryKey: queryServiceKeys.all });
      },
      onStatusEvent: handleStatusEvent,
      onHeartbeat: (heartbeatEvent) => {
        setLastHeartbeat(new Date(heartbeatEvent.timestamp));
      },
      onError: setError,
    });

    // Auto-connect on mount
    sseService.connect();

    // Cleanup on unmount
    return () => {
      sseService.disconnect();
      isInitialized.current = false;
    };
  }, [isEnabled, handleStatusEvent, queryClient]);

  // Keep visible query-service data fresh during a prolonged SSE outage.
  // Depend on the connected boolean so transitions between connecting and
  // reconnecting do not postpone the next refresh.
  const isDisconnected = isEnabled && connectionState !== 'connected';
  useEffect(() => {
    if (!isDisconnected) return;
    const refresh = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: queryServiceKeys.all });
    }, 15000);
    return () => clearInterval(refresh);
  }, [isDisconnected, queryClient]);

  /**
   * Manual connect function - resets reconnect attempts
   */
  const connect = useCallback(() => {
    sseService.resetReconnectAttempts();
    sseService.connect();
  }, []);

  /**
   * Manual disconnect function
   */
  const disconnect = useCallback(() => {
    sseService.disconnect();
  }, []);

  // Context value
  const value: SSEContextValue = {
    connectionState,
    clientId,
    lastEvent,
    lastHeartbeat,
    eventCount,
    error,
    isEnabled,
    connect,
    disconnect,
  };

  return <SSEContext.Provider value={value}>{children}</SSEContext.Provider>;
}

/**
 * Hook to access SSE context
 *
 * @throws Error if used outside of SSEProvider
 */
export function useSSEContext(): SSEContextValue {
  const context = useContext(SSEContext);
  if (!context) {
    throw new Error('useSSEContext must be used within an SSEProvider');
  }
  return context;
}
